import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Paper, Stack, TextField, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { importEncoderCsv } from '../api/encoderApi';
import { isAdminSession } from '../../../shared/auth/session';

const today = () => new Date().toISOString().slice(0, 10);

export default function ImportEncoderCsv() {
  const navigate = useNavigate();
  const isAdmin = isAdminSession();
  const [date, setDate] = useState(today());
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home', { replace: true });
    }
  }, [isAdmin, navigate]);

  const handleSubmit = async () => {
    if (submitting || !isAdmin) return;
    if (!file) {
      setError('Debe seleccionar el archivo CSV del ADR.');
      return;
    }
    if (!date) {
      setError('Debe indicar la fecha de la sesión.');
      return;
    }

    try {
      setError('');
      setSubmitting(true);
      const result = await importEncoderCsv({ file, date });
      setReport(result);
    } catch (importError) {
      console.error('Error importando CSV de encoder', importError);
      setError(importError?.response?.data?.message || 'No se pudo importar el archivo. Intente nuevamente.');
      setReport(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom color="#0D6AEE">
          Importar CSV del Encoder (ADR)
        </Typography>
        <Typography variant="body2">
          Sube el CSV diario exportado por el dispositivo ADR. El sistema calcula la mediana de velocidad,
          distancia y potencia de las 5 repeticiones de cada serie y completa los registros de Encoder que
          ya fueron creados ese día para cada paciente (según el número de serie indicado en su código de prueba).
          No crea pacientes ni registros nuevos.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Fecha de la sesión"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" component="label">
            {file ? file.name : 'Seleccionar CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Button>
          <Button variant="contained" color="primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Importando...' : 'Importar'}
          </Button>
        </Stack>
      </Paper>

      {report && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Resultado — {report.date}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {report.totalSeriesEnCsv} series detectadas en el CSV · {report.updated.length} registros actualizados
          </Typography>

          {report.updated.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Código prueba</TableCell>
                    <TableCell>Velocidad</TableCell>
                    <TableCell>Potencia</TableCell>
                    <TableCell>Distancia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.updated.map((row) => (
                    <TableRow key={row.codigo}>
                      <TableCell>{row.pacienteNombre ?? `#${row.pacienteId}`}</TableCell>
                      <TableCell>{row.codigoPrueba}</TableCell>
                      <TableCell>{row.before.velocidad} → {row.after.velocidad}</TableCell>
                      <TableCell>{row.before.potencia} → {row.after.potencia}</TableCell>
                      <TableCell>{row.before.distancia} → {row.after.distancia}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {report.sinCoincidenciaEnCsv.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {report.sinCoincidenciaEnCsv.length} registro(s) de Encoder de ese día no tienen una serie
              correspondiente completa en el CSV (código de prueba sin número de serie válido, o serie con
              menos de 5 repeticiones): {report.sinCoincidenciaEnCsv.map((r) => r.codigoPrueba || `#${r.codigo}`).join(', ')}
            </Alert>
          )}

          {report.seriesSinRegistro.length > 0 && (
            <Alert severity="info">
              El CSV trae {report.seriesSinRegistro.length} serie(s) sin un registro de Encoder creado ese día:
              {' '}{report.seriesSinRegistro.map((s) => `S${s.series}`).join(', ')}. Cree el registro del paciente
              correspondiente y vuelva a importar el CSV para completarlo.
            </Alert>
          )}
        </Paper>
      )}
    </Stack>
  );
}
